import axios, { AxiosResponse } from 'axios';
import { apiUrl } from './apiCaller';
import { clearSuperAdminToken, getSuperAdminToken } from './superAdminToken';

type SaMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE';

interface Options {
    /** Whether to attach the sa_access_token. Defaults to true. */
    auth?: boolean;
    /** Extra headers to merge into the request. */
    headers?: Record<string, string>;
}

/**
 * Axios wrapper for the super-admin API surface at `/api/v1/super-admin/*`.
 *
 * Reads the bearer token from sessionStorage (not localStorage) to keep the
 * super-admin session isolated from the tenant app. On 401 responses against
 * authed routes, clears the token so the route guard redirects to the login
 * page.
 */
export function saRequest<T = any>(
    method: SaMethod,
    path: string,
    data?: any,
    opts: Options = {},
): Promise<AxiosResponse<T>> {
    const authEnabled = opts.auth !== false;
    const url = apiUrl + 'super-admin/' + path.replace(/^\/+/, '');

    const headers: Record<string, string> = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(opts.headers || {}),
    };

    if (authEnabled) {
        const token = getSuperAdminToken();
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
    }

    const config = { headers };

    let p: Promise<AxiosResponse<T>>;
    switch (method) {
        case 'POST':
            p = axios.post(url, data ?? {}, config);
            break;
        case 'GET':
            p = axios.get(url, config);
            break;
        case 'PATCH':
            p = axios.patch(url, data ?? {}, config);
            break;
        case 'DELETE':
            p = axios.delete(url, { ...config, data });
            break;
    }

    return p!.catch((err: any) => {
        const status = err?.response?.status;
        if (authEnabled && status === 401) {
            // Token expired / IP-changed / deactivated — wipe so the guard redirects.
            clearSuperAdminToken();
        }
        throw err;
    });
}
